import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { Drawer } from 'ui-components/components/drawer/drawer';
import { DrawerHeader } from 'ui-components/components/drawer/drawer-header';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_INFO,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import { messages } from './messages';
import { INVENTORY_UOM_SCAN_TO_RECEIVE_SUBMITTED_WITH_UOM } from '../../constants/analytics-events';
import { Features } from '../../constants/features';
import { useSendMetrics } from '../../hooks/use-send-metrics';
import { unspecifiedWarehouseLocation } from '../../constants/unspecified-warehouse-location';
import { useEnableLotTracking } from '../../feature-flags/hooks/use-enable-lot-tracking';
import { useIsUnitsOfMeasureAvailable } from '../../feature-gates/use-is-units-of-measure-available';
import { useIsAlphaUser } from '../../hooks/account-information/use-is-alpha-user';
import { useBinaryState } from '../../hooks/use-binary-state';
import { useLuxonUserDateFormat } from '../../hooks/use-datetime-formats';
import { useModal } from '../../hooks/use-modal';
import { useSimpleToggle } from '../../hooks/use-simple-toggle';
import { getDateAsUtcNoonInISO } from '../../services/datetime-service';
import { SCAN_TO_RECEIVE_JOB_COMPLETE } from '../../services/pusher-service/pusher-contract-messages';
import { importProcessRequested } from '../../store/csv-import/csv-import.actions';
import Global from '../../store/global-selectors';
import { Workflows } from '../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { ImportType } from '../csv-import/data/types/import-type';
import { PromotionalBanner } from '../promotional-banner';
import { SimpleDropdown } from '../simple-dropdown';
import { SimpleModal } from '../simple-modal/simple-modal';
import { ScanProduct } from './scan-product/scan-product';
import { ScanToReceiveTable } from './scan-to-receive-table/scan-to-receive-table';
import { submitScanToReceive } from './scan-to-recieve.api';
import styles from './styles.module.less';

const warehouseSelector = createSelector([Global.selectSellerInventoryWarehouses], (warehouses) => {
  return warehouses.map((warehouse) => ({
    key: warehouse.inventoryWarehouseId,
    name: warehouse.name,
    ...warehouse,
  }));
});

/**
 * @param {object} props
 * @param {string} [props.defaultWarehouseId]
 * @param {() => void} props.onClose
 * @returns {React.ReactNode}
 */
export const ScanToReceiveDrawer = ({ onClose, defaultWarehouseId }) => {
  const { formatMessage } = useIntl();

  const warehouses = useSelector(warehouseSelector);
  const [warehouseId, setWarehouseId] = useState(defaultWarehouseId ?? (warehouses.length === 1 ? warehouses[0].key : ''));

  const [notes, setNotes] = useState('');
  const [scannedProducts, setScannedProducts] = useState(/** @type {Array<import('./types').ScannedProduct>} */ ([]));

  const dispatch = useDispatch();

  const [isSubmitting, setStartSubmitting, setStopSubmitting] = useBinaryState();
  const [showLocationErrors, setShowLocationErrors] = useState(false);

  const sellerId = useSelector(Global.selectSellerSellerId);
  const userDateFormat = useLuxonUserDateFormat();
  const isEarlyAccess = useIsAlphaUser(Features.InventoryReceiving);

  const [notificationId, setNotificationId] = useState();

  const removeProductNotification = useCallback(() => {
    const notifications = NotificationService.getNotifications();
    if (!notifications[notificationId]) {
      setNotificationId(undefined);
      return;
    }
    NotificationService.removeNotification(notificationId);
  }, [notificationId]);

  const trackWorkflow = useTrackWorkflow(Workflows.SCAN_TO_RECEIVE_FLOW);
  const trackMetrics = useSendMetrics();

  const isLotTrackingEnabled = useEnableLotTracking();
  const { isAvailable: isUnitsOfMeasureEnabled } = useIsUnitsOfMeasureAvailable();

  useEffect(() => {
    trackWorkflow(WorkflowSteps.START, { sellerId });
  }, [sellerId, trackWorkflow]);

  const hasProductsWithoutLocation = useMemo(() => {
    return scannedProducts.some((product) => {
      if (product.isBundle) {
        return product.components?.some((component) => !component.locationId);
      }
      return !product.locationId;
    });
  }, [scannedProducts]);

  const handleSubmit = useCallback(async () => {
    if (hasProductsWithoutLocation) {
      setShowLocationErrors(true);
      return;
    }

    removeProductNotification();

    setStartSubmitting();

    /**
     * @param {import('./types').ScannedProduct | import('./types').BundleItem} product
     * @param {number} quantity - for non-bundle products this is always 1, for bundle products this is the quantity of the bundle, which is always >= 1
     * @returns {import('./scan-to-recieve.api').Product}
     */
    const mapProductToItem = (product, quantity) => ({
      productId: product.productId,
      locationId: product.locationId,
      productName: product.name,
      sku: product.sku,
      cost: product.cost,
      quantity: product.quantity * (product.eaches ?? 1) * quantity,
      lot: product.lotId,
      expirationDate: getDateAsUtcNoonInISO(product.expirationDate, userDateFormat),
    });

    const request = {
      inventoryWarehouseId: warehouseId,
      notes,
      items: scannedProducts.flatMap((product) => {
        if (product.components && product.components.length > 0) {
          return product.components.map((component) => mapProductToItem(component, product.quantity));
        }
        return [mapProductToItem(product, 1)];
      }),
    };

    try {
      const data = await submitScanToReceive(request);
      dispatch(
        importProcessRequested({
          jobId: data.jobId,
          importType: ImportType.scanToReceive,
          pusherEvent: SCAN_TO_RECEIVE_JOB_COMPLETE,
          customSummaryNotification: {
            onSuccess: () =>
              NotificationService.showNotification({
                notificationType: NOTIFICATION_TYPE_SUCCESS,
                title: <FormattedMessage {...messages.scanToReceiveSuccessTitle} />,
                content: <FormattedMessage {...messages.scanToReceiveSuccessContent} />,
                dontHide: true,
              }),
            onFail: () =>
              NotificationService.showNotification({
                notificationType: NOTIFICATION_TYPE_ERROR,
                title: <FormattedMessage {...messages.scanToReceiveFailTitle} />,
                content: <FormattedMessage {...messages.scanToReceiveFailContent} />,
                dontHide: true,
              }),
          },
        }),
      );
      trackWorkflow(WorkflowSteps.COMPLETE, { request });

      if (isUnitsOfMeasureEnabled && scannedProducts.some((p) => p.uomId != null && p.uomId !== 0)) {
        const uomRows = scannedProducts.filter((p) => p.uomId != null && p.uomId !== 0);
        trackMetrics(INVENTORY_UOM_SCAN_TO_RECEIVE_SUBMITTED_WITH_UOM, {
          productsWithUomCount: uomRows.length,
          totalProductsCount: scannedProducts.length,
          uomTypeNames: [...new Set(uomRows.map((p) => p.uomTypeName).filter(Boolean))],
          totalEaches: scannedProducts.reduce((sum, p) => sum + p.quantity * (p.eaches ?? 1), 0),
        });
      }

      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_INFO,
        title: <FormattedMessage {...messages.startProcessingProductsTitle} />,
        content: <FormattedMessage {...messages.startProcessingProductsContent} />,
        duration: NOTIFICATION_DURATIONS.MEDIUM,
      });
      trackWorkflow(WorkflowSteps.CLOSE);
      onClose();
    } catch (error) {
      trackWorkflow(WorkflowSteps.ERROR, { request, error });
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.errorSubmitFailedTitle} />,
        content: <FormattedMessage {...messages.errorSubmitFailedContent} />,
      });
    } finally {
      setStopSubmitting();
    }
  }, [
    hasProductsWithoutLocation,
    removeProductNotification,
    setStartSubmitting,
    warehouseId,
    notes,
    scannedProducts,
    trackWorkflow,
    trackMetrics,
    isUnitsOfMeasureEnabled,
    dispatch,
    onClose,
    setStopSubmitting,
    userDateFormat,
  ]);

  const { open, isOpen, close } = useModal();

  const getRowKey = useCallback(
    (product) => (isUnitsOfMeasureEnabled && product.uomId > 0 ? `${product.productId}_${product.uomId}` : String(product.productId)),
    [isUnitsOfMeasureEnabled],
  );

  const confirmAction = () => {
    setScannedProducts([]);
    removeProductNotification();
    close();
  };

  const handleProductNotification = useCallback(
    /**
     * @param {string} productName
     * @param {number} quantity
     * @param {import('./types').ScannedProduct} product
     */
    (productName, quantity, product) => {
      const isUomRow = isUnitsOfMeasureEnabled && product.uomId != null && product.uomId !== 0;
      const notificationConfig = {
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <FormattedMessage {...messages.startProcessingProductsTitle} />,
        content: (
          <FormattedMessage
            {...messages.productFoundAndAdded}
            values={{
              name: productName,
              quantity,
              hasUom: isUomRow,
              eaches: quantity * (product.eaches ?? 1),
            }}
          />
        ),
        dontHide: true,
      };

      const notifications = NotificationService.getNotifications();
      if (notifications[notificationId]) {
        NotificationService.updateNotification(notificationId, notificationConfig);
      } else {
        const id = NotificationService.showNotification(notificationConfig);
        setNotificationId(id);
      }
    },
    [notificationId, isUnitsOfMeasureEnabled],
  );

  const addScannedProduct = useCallback(
    /**
     * @param {import('./types').ScannedProduct} newScannedProduct
     */
    (newScannedProduct) => {
      const newRowKey = getRowKey(newScannedProduct);
      const productWithRowKey = { ...newScannedProduct, rowKey: newRowKey };

      setScannedProducts((prev) => {
        const existing = prev.find((sp) => sp.rowKey === newRowKey);
        if (!existing) {
          handleProductNotification(newScannedProduct.name, newScannedProduct.quantity, newScannedProduct);
          return [...prev, productWithRowKey];
        }
        const quantity = existing.quantity + 1;
        handleProductNotification(newScannedProduct.name, quantity, newScannedProduct);
        return prev.map((sp) => (sp.rowKey === newRowKey ? { ...sp, quantity } : sp));
      });
    },
    [getRowKey, handleProductNotification],
  );

  const totalEaches = useMemo(() => scannedProducts.reduce((sum, p) => sum + p.quantity * (p.eaches ?? 1), 0), [scannedProducts]);

  const hasUomProducts = useMemo(
    () => isUnitsOfMeasureEnabled && scannedProducts.some((p) => p.uomId != null),
    [isUnitsOfMeasureEnabled, scannedProducts],
  );

  const onCloseDrawer = () => {
    trackWorkflow(WorkflowSteps.CANCEL);
    onClose();
    removeProductNotification();
  };

  const [showAdvancedColumns, toggleAdvancedColumns] = useSimpleToggle();

  const { open: openWarehouseModal, close: closeWarehouseModal, isOpen: isOpenWarehouseModal } = useModal();
  const [temporaryWarehouseId, setTemporaryWarehouseId] = useState(null);
  const warehouseLocations = useSelector((state) =>
    Global.selectSellerInventoryWarehouseLocationsByWarehouseId(state, {
      inventoryWarehouseId: warehouseId,
    }),
  );

  const warehouseLocation = useMemo(() => {
    return warehouseLocations.find((wl) => wl?.area === unspecifiedWarehouseLocation);
  }, [warehouseLocations]);

  const onOpenWarehouseModal = useCallback(
    (newWarehouseId) => {
      const hasLocationConflict = scannedProducts.some((product) => {
        if (product.locationId && product.locationId !== warehouseLocation?.inventoryLocationId) {
          return true;
        }
        if (product.components && product.components.length > 0) {
          return product.components.some(
            (component) => component.locationId && component.locationId !== warehouseLocation?.inventoryLocationId,
          );
        }
        return false;
      });

      if (hasLocationConflict) {
        setTemporaryWarehouseId(newWarehouseId);
        openWarehouseModal();
      } else {
        setWarehouseId(newWarehouseId);
        setScannedProducts((prev) =>
          prev.map((product) => ({
            ...product,
            locationId: warehouseLocation?.inventoryLocationId,
            components: product.components?.map((component) => ({
              ...component,
              locationId: warehouseLocation?.inventoryLocationId,
            })),
          })),
        );
      }
    },
    [warehouseLocation, openWarehouseModal, scannedProducts],
  );

  const confirmWarehouseChange = useCallback(() => {
    setScannedProducts((prev) =>
      prev.map((product) => ({
        ...product,
        locationId: warehouseLocation?.inventoryLocationId,
        components: product.components?.map((component) => ({
          ...component,
          locationId: warehouseLocation?.inventoryLocationId,
        })),
      })),
    );
    setWarehouseId(temporaryWarehouseId);
    closeWarehouseModal();
  }, [warehouseLocation, closeWarehouseModal, temporaryWarehouseId]);

  return (
    <Drawer className={styles.drawer} header={<FormattedMessage {...messages.header} />} onCancel={onCloseDrawer} onClose={onCloseDrawer}>
      <DrawerHeader
        closeButtonAriaLabel={messages.closeDrawerButtonAriaLabel}
        title={
          <div className={styles['header-container']}>
            <FormattedMessage {...messages.header} />
            {isEarlyAccess ? <Text className={styles['early-access-tag']} textType={TextType.Caption} value={messages.tagText} /> : null}
          </div>
        }
      />
      <div className={styles['drawer-content']}>
        <section className={styles['drawer-section']}>
          {isEarlyAccess ? (
            <PromotionalBanner
              className={styles['promotional-banner']}
              promotionalHeaderMessage={<Text element='span' textType={TextType.Paragraph} value={messages.bannerContent} />}
            >
              <Button className={styles['feedback-link']} href='https://form.jotform.com/242334908080151' theme={THEME_CONSTANTS.link}>
                <FormattedMessage {...messages.shareFeedback} />
                <FontAwesomeIcon className={styles.icon} icon={faArrowRight} />
              </Button>
            </PromotionalBanner>
          ) : null}
          <Text className={styles['instruction-text']} textType={TextType.H4} value={messages.scanInstructions} />
          <div className={styles['form-container']}>
            <div className={styles['form-left-column']}>
              <Text className={styles['select-warehouse-text']} textType={TextType.Label} value={messages.selectWarehouse} />
              <SimpleDropdown
                defaultTitle={<FormattedMessage {...messages.selectWarehouse} />}
                dropdownMenuShouldHaveWidthOfButton
                items={warehouses}
                onItemSelected={(key) => {
                  setTemporaryWarehouseId(key);
                  onOpenWarehouseModal(key);
                }}
                selectedItemKey={warehouseId}
              />
              <Text className={styles['add-product-text']} textType={TextType.Label} value={messages.addProducts} />
              <ScanProduct
                allowBundles
                disabled={!warehouseId}
                includeUom={isUnitsOfMeasureEnabled}
                onFailedScan={removeProductNotification}
                onSuccessfulScan={addScannedProduct}
                trackWorkflow={trackWorkflow}
                warehouseId={warehouseId}
              />
            </div>
            <div className={styles['form-right-column']}>
              <div>
                <Text textType={TextType.Label} value={messages.notes} />
                <Text className={styles['optional-text']} element='span' textType={TextType.Caption} value={messages.optional} />
              </div>
              <FloatTextArea maxLength={1000} onChange={setNotes} value={notes} />
            </div>
          </div>
        </section>
        <section className={styles['drawer-section']}>
          <Text
            className={styles['section-title']}
            formattedMessageValues={{
              productCount: scannedProducts.length,
              label: (chunks) => <span>{chunks}</span>,
            }}
            textType={TextType.H4}
            value={messages.addedProducts}
          />
          {hasUomProducts ? (
            <Text formattedMessageValues={{ count: totalEaches }} textType={TextType.Caption} value={messages.totalEaches} />
          ) : null}
          <div className={styles['text-row']}>
            {scannedProducts.length > 0 && (
              <Button disabled={!scannedProducts.length} onClick={open} theme={THEME_CONSTANTS.link}>
                <Text
                  formattedMessageValues={{
                    icon: <FontAwesomeIcon className={styles['xmark-icon']} icon={faXmark} />,
                  }}
                  textType={TextType.Caption}
                  value={messages.clearAll}
                />
              </Button>
            )}
            {isLotTrackingEnabled ? (
              <Checkbox
                checked={showAdvancedColumns}
                className={styles['toggle-advanced-fields']}
                label={formatMessage(messages.showAdvancedFields)}
                onChange={toggleAdvancedColumns}
              />
            ) : null}
          </div>
          <ScanToReceiveTable
            isUnitsOfMeasureEnabled={isUnitsOfMeasureEnabled}
            scannedProducts={scannedProducts}
            setScannedProducts={setScannedProducts}
            showAdvancedColumns={showAdvancedColumns}
            showLocationErrors={showLocationErrors}
            warehouseId={warehouseId}
          />
        </section>
      </div>
      <div className={styles['drawer-footer']}>
        <Button onClick={onCloseDrawer} theme={THEME_CONSTANTS.secondary}>
          <FormattedMessage {...messages.cancel} />
        </Button>
        <Button
          disabled={!warehouseId || scannedProducts.length === 0 || isSubmitting}
          isLoading={isSubmitting}
          onClick={handleSubmit}
          theme={THEME_CONSTANTS.primary}
        >
          <FormattedMessage {...messages.submit} />
        </Button>
      </div>
      {isOpenWarehouseModal ? (
        <SimpleModal
          actionMessage={messages.modalConfirm}
          cancelMessage={messages.modalCancel}
          headerMessage={messages.modalTitle}
          onAction={confirmWarehouseChange}
          onCancel={closeWarehouseModal}
          onClose={closeWarehouseModal}
          size='medium'
        >
          <Text className={styles.description} textType={TextType.Paragraph} value={messages.modalMessage} />
        </SimpleModal>
      ) : null}
      {isOpen ? (
        <SimpleModal
          actionMessage={messages.confirm}
          cancelMessage={messages.cancel}
          headerMessage={messages.confirmationModalHeader}
          onAction={confirmAction}
          onCancel={close}
          onClose={close}
          size='medium'
        >
          <Text textType={TextType.Paragraph} value={messages.confirmationModalContent} />
        </SimpleModal>
      ) : null}
    </Drawer>
  );
};
